import { CommonModule, NgPlural, NgPluralCase } from '@angular/common';
import { Component, DestroyRef, Injector, inject, input, type OnInit, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { AvatarModule } from '@openng/optimus-ui/avatar';
import { ButtonModule } from '@openng/optimus-ui/button';
import { SelectModule } from '@openng/optimus-ui/select';
import { TextareaModule } from '@openng/optimus-ui/textarea';
import { TooltipModule } from '@openng/optimus-ui/tooltip';
import type { LoadOptions } from 'devextreme/data';
import CustomStore from 'devextreme/data/custom_store';
import DataSource from 'devextreme/data/data_source';
import { DxDataGridModule } from 'devextreme-angular/ui/data-grid';
import { isNil, sortBy } from 'lodash-es';
import { injectParams } from 'ngxtension/inject-params';
import { catchError, lastValueFrom, map, type Observable, of } from 'rxjs';

import type { UserAdapter } from '@app/auth/models/user.model';
import type { FoundationModelService } from '@app/modules/assets/services/foundation-model.service';
import type { PipelineService } from '@app/modules/assets/services/pipeline-builder.service';
import type { PromptService } from '@app/modules/assets/services/prompt.service';
import type { RagService } from '@app/modules/assets/services/rag.service';
import type { ApprovalReview } from '@app/shared/components/approval-review-workflow/approval-review-workflow/approval-review-workflow.component';
import { DescriptionComponent } from '@app/shared/components/description/description.component';
import { DialogBoxComponent } from '@app/shared/components/dialog-box/dialog-box.component';
import { InputContainerComponent } from '@app/shared/components/input-container/input-container.component';
import { StatusIconComponent } from '@app/shared/components/status-icon/status-icon.component';
import {
    APPROVAL_TYPE,
    OBJECT_DISPLAY_NAMES,
    type OBJECTS,
    OK_RESPONSE,
    REVIEW_STATUS,
    WORKFLOW_STATUS,
} from '@app/shared/constants/shared.constant';
import type { ImpactAssessmentList } from '@app/shared/interface/common.interface';
import {
    type ApprovalWorkflowAdapter,
    Workflow,
    type WorkflowFormGroup,
} from '@app/shared/models/workflow.model';
import { ApprovalWorkflowService } from '@app/shared/services/approval-workflow.service';
import { CommonService } from '@app/shared/services/common.service';
import type { ListResponse } from '@app/shared/services/mixins/crud.service';
import { ToastNotificationService } from '@app/shared/services/toast-notification.service';
import { FormHelper } from '@app/shared/utils/form-helper';
import { Helpers } from '@app/shared/utils/helpers';
import { OBJECT_SERVICE_MAPPER } from '@app/shared/utils/object-service-mapper';
import { RouterHelper } from '@app/shared/utils/router-helper';
@Component({
    selector: 'cor-approval-action',
    templateUrl: './approval-action.component.html',
    styleUrls: ['./approval-action.component.scss'],
    imports: [
        CommonModule,
        ReactiveFormsModule,
        InputContainerComponent,
        SelectModule,
        RouterModule,
        TextareaModule,
        DialogBoxComponent,
        StatusIconComponent,
        DxDataGridModule,
        TooltipModule,
        NgPlural,
        FormsModule,
        ButtonModule,
        NgPluralCase,
        DescriptionComponent,
        AvatarModule,
    ],
})
export class ApprovalActionComponent implements OnInit {
    protected readonly currentProject = injectParams<string>('projectSlug');
    readonly summaryDatasource = input<ApprovalReview[]>([]);

    /**
     * entity id
     * e.g. model_id, feature_id
     */
    readonly id = input<number>();

    /**
     * Object Permission to populate the reviewers
     */
    readonly object = input<OBJECTS>();

    readonly workflowId = input<number>();

    protected readonly REVIEW_STATUS = REVIEW_STATUS;
    protected readonly OBJECT_DISPLAY_NAMES = OBJECT_DISPLAY_NAMES;

    form: WorkflowFormGroup;
    protected readonly workflow = signal<ApprovalWorkflowAdapter>(null);

    protected requestApprovalPopup = {
        isVisible: signal(false),
        isLoading: signal(false),
    };

    readonly reviewerList = signal<UserAdapter[]>([]);
    readonly isSaving = signal(false);
    protected readonly isBatchApprovalPopupVisible = signal(false);
    protected readonly batchApprovalPopupTitle = signal<string>(null);
    protected readonly isGroupedApproval = signal(false);
    datasource: DataSource<ImpactAssessmentList, number>;
    protected readonly isPendingApprovalPresent = signal(false);
    protected approvalSequence: ImpactAssessmentList[];
    service: FoundationModelService | PromptService | RagService | PipelineService;
    private readonly destroyRef = inject(DestroyRef);
    private readonly injector = inject(Injector);
    private readonly commonService = inject(CommonService);
    private readonly approvalWorkflow = inject(ApprovalWorkflowService);
    private readonly route = inject(ActivatedRoute);
    private readonly router = inject(Router);
    private readonly toast = inject(ToastNotificationService);
    ngOnInit() {
        this.service = this.injector.get<any>(OBJECT_SERVICE_MAPPER[this.object()]);
    }

    private setForm(): void {
        this.form = Workflow.getForm(
            new Workflow({
                id: null,
                status: null,
                newStatus: REVIEW_STATUS.PENDING_ACCEPTANCE,
                responsibilityId:
                    this.workflow().responsibilities.length === 1
                        ? this.workflow().responsibilities[0].id
                        : null,
            }),
        );
    }

    protected onShowRequestApprovalPopup(): void {
        this.requestApprovalPopup.isLoading.set(true);
        let workflow$: Observable<ApprovalWorkflowAdapter>;
        if (isNil(this.workflow()) || this.workflowId() !== this.workflow()?.id) {
            workflow$ = this.approvalWorkflow.getById(this.workflowId()).pipe(
                takeUntilDestroyed(this.destroyRef),
                map(response => response.result),
            );
        } else {
            workflow$ = of(this.workflow());
        }

        workflow$.subscribe(workflow => {
            if (this.workflowId() !== this.workflow()?.id) this.workflow.set(workflow);
            this.setForm();
            this.service
                .getEntityStatusChangeApproval(this.id(), WORKFLOW_STATUS.PENDING_APPROVAL, false)
                .pipe(takeUntilDestroyed(this.destroyRef))
                .subscribe({
                    next: response => {
                        if (response?.result?.status === OK_RESPONSE) {
                            this.requestApprovalPopup.isVisible.set(true);
                        }
                    },
                    error: error => {
                        const message = Helpers.getErrorMessageString(error);
                        if (!message) {
                            console.error('Unexpected error when requesting approval:', error);
                            return;
                        }
                        this.approvalSequence = error?.error?.data?.result || [];

                        if (this.approvalSequence.length === 0) {
                            // displaying toast message in place of error popup
                            this.toast.showError(message);

                            return;
                        }

                        const pendingApprovalSequence = this.approvalSequence.filter(
                            (e: ImpactAssessmentList) =>
                                [
                                    WORKFLOW_STATUS.PENDING_APPROVAL,
                                    WORKFLOW_STATUS.REJECTED,
                                ].includes(e.currentStatus),
                        );
                        if (pendingApprovalSequence.length > 0) {
                            this.approvalSequence = pendingApprovalSequence;
                            this.isPendingApprovalPresent.set(true);

                            this.batchApprovalPopupTitle.set(
                                this.approvalSequence.length === 1
                                    ? '1 Input needs attention'
                                    : `${this.approvalSequence.length} ${'Inputs need attention'}`,
                            );
                        }

                        this.isBatchApprovalPopupVisible.set(true);
                        this.loadList(of({ result: this.approvalSequence }));
                    },
                })
                .add(() => this.requestApprovalPopup.isLoading.set(false));
        });
    }

    onCancelClick(): void {
        this.form.reset();
        this.isGroupedApproval.set(false);
    }

    onSubmitClick(): void {
        if (!this.form.valid) {
            FormHelper.showFormErrors(this.form, this.toast);

            return;
        }
        this.isSaving.set(true);
        const { id, comment, responsibilityId } = this.form.value;

        const summaries = (this.summaryDatasource() || []).filter(
            e => e.status !== REVIEW_STATUS.CANCELED && e.type === APPROVAL_TYPE.GROUPED,
        );

        const payload = {
            id,
            comment,
            responsibilityId,
            status: REVIEW_STATUS.PENDING_ACCEPTANCE,
            ...((this.isGroupedApproval() || summaries.length > 0) && {
                type: APPROVAL_TYPE.GROUPED,
            }),
            isSpecific: false,
        };

        this.service
            .updateReviewStatus(this.id(), { entityId: this.id(), ...payload })
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(() => {
                this.commonService.showToastForStatusChange(REVIEW_STATUS.PENDING_ACCEPTANCE);
                // NOTE: We reload the page so that the workflow status in the tabs component is refreshed
                RouterHelper.reloadPage(this.router, this.route);
            })
            .add(() => {
                this.isSaving.set(false);
                // FIXME: We are closing the popup here. This code is only relevang in the case of an error.
                //        For successful API call - we reload the page above
                //        For a failed API call - we show a error-toast and close the popup - which seems odd.
                //           as the user should be allowed to retry the review request
                this.requestApprovalPopup.isVisible.set(false);
                this.onCancelClick();
            });
    }

    onBatchApprovalPopupCancelClick(): void {
        this.isBatchApprovalPopupVisible.set(false);
        this.isPendingApprovalPresent.set(false);
    }

    protected loadList(data: Observable<ListResponse<ImpactAssessmentList>>) {
        this.datasource = new DataSource({
            store: new CustomStore({
                load: (loadOptions: LoadOptions) => {
                    delete loadOptions.userData;

                    return lastValueFrom(
                        data.pipe(
                            map(resp => {
                                const result = sortBy(
                                    resp.result.map(d => ({
                                        ...d,
                                        link: RouterHelper.buildRoute({
                                            object: d.objectType,
                                            id: d.id,
                                            projectSlug: this.currentProject(),
                                        }),
                                    })),
                                    ['batchId', 'name'],
                                );

                                return {
                                    data: result || [],
                                    totalCount: resp.pageInfo ? resp.pageInfo.total : result.length,
                                };
                            }),
                            catchError(() => {
                                // Catch the error and return empty data so we don't show [object Object]
                                // in the UI in case of errors.
                                return of({ data: [], totalCount: 0 });
                            }),
                        ),
                    );
                },
            }),
        });
    }

    onGroupApprovalReject() {
        this.isPendingApprovalPresent.set(false);
        this.isBatchApprovalPopupVisible.set(false);
        this.isGroupedApproval.set(false);
        this.toast.showError('Unable to send for approval, all inputs need to be approved.');
    }

    onGroupApprovalConfirm() {
        this.isPendingApprovalPresent.set(false);
        this.isBatchApprovalPopupVisible.set(false);
        this.isGroupedApproval.set(true);

        // NOTE: This is done for User Experience. One popup closes and after 0.5s another popup appears.
        setTimeout(() => {
            this.requestApprovalPopup.isVisible.set(true);
        }, 500);
    }
}
